import { DateTime, Duration } from 'luxon';
import {
    TherapySession,
    TherapySessionFormat,
    TherapySessionType,
} from '../types';
import { GQLContractWithoutClientConsentsAndClientOverviewFragment } from '@upheal/graphql/definitions/contracts/ContractWithoutClientConsentsAndClientOverviewFragment.generated';
import { isCoupleContract } from './contractUtils';
import {
    GQLLegacySessionDocumentType,
    GQLSessionNoteType,
    GQLTherapySessionAnonymousParticipantsType,
} from '@upheal/graphql/definitions/schema.generated';
import pluralize from 'pluralize';

export function sessionName(
    session?: Pick<TherapySession, 'sessionName' | 'counter'> | null,
) {
    const name = session?.sessionName
        ? session.sessionName
        : session?.counter
        ? `Session #${session.counter}`
        : 'Session';
    return name;
}

export const allContractsAreDemo = (
    contracts: GQLContractWithoutClientConsentsAndClientOverviewFragment[],
) => {
    return contracts.every(isDemoContract);
};

export const isDemoContract = (
    contract: GQLContractWithoutClientConsentsAndClientOverviewFragment,
) => {
    // TODO, migrate to contract.isDemo once the backend is updated
    return contract.clients.some((c) => c.isDemo);
};

export function isDemoSession(
    contract: GQLContractWithoutClientConsentsAndClientOverviewFragment,
    session: TherapySession,
) {
    return isDemoContract(contract) && session.counter === 1;
}

/// Date and time utils

export function getTimeIntervalUpToMinutesString(interval: number) {
    const intervalSeconds = Math.floor(interval / 1000) % 60;
    const intervalMinutes = Math.floor(interval / (1000 * 60));
    const intervalString = `${String(intervalMinutes).padStart(
        2,
        '0',
    )}:${String(intervalSeconds).padStart(2, '0')}`;
    return intervalString;
}

export function getTimeIntervalString(interval: number) {
    const intervalSeconds = Math.floor(interval / 1000) % 60;
    const intervalMinutes = Math.floor(interval / (1000 * 60)) % 60;
    const intervalHours = Math.floor(interval / (1000 * 60 * 60)) % 24;

    const time = `${intervalHours}:${String(intervalMinutes).padStart(
        2,
        '0',
    )}:${String(intervalSeconds).padStart(2, '0')}`;
    return time;
}

export function sessionTimeToFormattedString(dateString: string) {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
    };

    return date.toLocaleTimeString(undefined, options).toUpperCase();
}

export function sessionDateStringToFormattedString(
    dateString: string,
    dateOnly: boolean = false,
) {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...(!dateOnly && {
            hour: 'numeric',
            minute: 'numeric',
        }),
    };

    return date.toLocaleDateString(undefined, options);
}

export function sessionDateTimeToFormattedString(
    date: DateTime,
    dateOnly: boolean = false,
) {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...(!dateOnly && {
            hour: 'numeric',
            minute: 'numeric',
        }),
    };

    return date.toLocaleString(options);
}

export function sessionDurationMsToMinutes(
    duration: number | null | undefined,
) {
    return duration ? Math.floor(duration / 1000 / 60) : 0;
}

export function sessionDurationMsToMinutesString(
    duration: number | null | undefined,
    includeMinutes?: boolean,
) {
    const durationMinutes = sessionDurationMsToMinutes(duration);

    return includeMinutes
        ? pluralize('minute', durationMinutes, true) //
              .replace(' ', '\xa0') // non-breaking space
        : durationMinutes.toString();
}

export function sessionDurationToMinutesString(
    duration: Duration | null | undefined,
) {
    return duration ? Math.ceil(duration.as('minutes')).toFixed() : '0';
}

export function sessionDurationMsToSecondsOrMinutesString(
    durationMs: number,
): string {
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes >= 1) {
        return pluralize('minute', minutes, true);
    } else {
        return pluralize('second', seconds, true);
    }
}

export function dateTimeFromComponents({
    date,
    time,
    timezoneId,
}: {
    date: DateTime;
    time: DateTime;
    timezoneId: string;
}) {
    const { hour, minute } = time.toObject();
    return date
        .set({ hour, minute, second: 0, millisecond: 0 })
        .setZone(timezoneId, { keepLocalTime: true });
}

export function dateFromComponents({
    date,
    time,
    timezoneId,
}: {
    date: DateTime;
    time: DateTime;
    timezoneId: string;
}) {
    return dateTimeFromComponents({ date, time, timezoneId }).toJSDate();
}

export const relativeTimestamp = (dateTime: DateTime) => {
    return dateTime.diffNow().as('milliseconds') * -1;
};

export const getTodayOrTomorrow = (day: DateTime): string | null => {
    const today = DateTime.now();
    const tomorrow = today.plus({ days: 1 });
    if (day.hasSame(today, 'day')) return 'Today';
    if (day.hasSame(tomorrow, 'day')) return 'Tomorrow';
    return null;
};

/// Misc

export const SessionTypeToLabelMap: Record<
    TherapySessionType | GQLSessionNoteType,
    string
> = {
    [TherapySessionType.Intake]: 'Intake',
    [TherapySessionType.Bps]: 'Biopsychosocial intake assessment',
    [TherapySessionType.Standard]: 'Upheal',
    [TherapySessionType.Soap]: 'SOAP',
    [TherapySessionType.GpSoap]: 'SOAP',
    [TherapySessionType.HeadwaySoap]: 'SOAP',
    [TherapySessionType.Dap]: 'DAP',
    [TherapySessionType.Emdr]: 'EMDR',
    [TherapySessionType.Girp]: 'GIRP',
    [TherapySessionType.Birp]: 'BIRP',
    [TherapySessionType.Custom]: 'Custom',
    [TherapySessionType.PsychiatricSoap]: 'Psychiatric SOAP',
    [TherapySessionType.Mse]: 'Mental Status Exam',
    [TherapySessionType.PsychiatricIntake]: 'Psychiatric Intake',
    [TherapySessionType.CoupleStandard]: 'Upheal for couples',
    [TherapySessionType.CoupleSoap]: 'SOAP for couples',
    [TherapySessionType.CoupleDap]: 'DAP for couples',
    [TherapySessionType.Template]: 'Template',
} as const;

const SessionFormatToLabel: Record<TherapySessionFormat, string> = {
    [TherapySessionFormat.InPerson]: 'in-person',
    [TherapySessionFormat.Online]: 'online',
};

export const sessionTimeString = (session: TherapySession) => {
    const durationString =
        sessionDurationMsToMinutesString(
            session.duration ?? session.recordingDuration,
        ) + ' min';

    const startTime = DateTime.fromISO(session.date);
    const endTime = startTime.plus({
        milliseconds: session.duration ?? session.recordingDuration,
    });
    const startTimeString = startTime.toFormat('t');
    const endTimeString = endTime.toFormat('t');
    const timeString = `${startTimeString} - ${endTimeString}, ${durationString}`;
    return timeString;
};

export const sessionFormatString = (
    session: TherapySession,
    contract?: GQLContractWithoutClientConsentsAndClientOverviewFragment,
    t?: (key: string) => string,
) => {
    const dateString = session.date
        ? sessionDateStringToFormattedString(session.date, true)
        : '';

    const isCouples =
        (contract && isCoupleContract(contract)) ||
        session.anonymousParticipantsType ===
            GQLTherapySessionAnonymousParticipantsType.Couple;

    const sessionType = t
        ? isCouples
            ? t('session.format.couples')
            : t('session.format.individual')
        : isCouples
        ? 'Couples'
        : 'Individual';

    const format = session.sessionFormat
        ? ' ' +
          (t
              ? session.sessionFormat === TherapySessionFormat.InPerson
                  ? t('session.format.inPerson')
                  : t('session.format.online')
              : SessionFormatToLabel[session.sessionFormat])
        : '';

    const sessionOn = t ? t('session.format.sessionOn') : 'session on';

    return `${sessionType}${format} ${sessionOn} ${dateString}`;
};

export const legacyDocumentTypeToTitle = (
    legacyType?: GQLLegacySessionDocumentType | null,
) => {
    switch (legacyType) {
        case GQLLegacySessionDocumentType.ProgressNotes:
            return 'Progress Note';
        case GQLLegacySessionDocumentType.ClientSummary:
            return 'Post-session Summary';
        case GQLLegacySessionDocumentType.PrivateNotes:
            return 'Private note';
        default:
            return 'Note';
    }
};
